import { config } from "@/config";
import { ReactNode, useState } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { HopeifierLogo } from "./HopifierLogo";
import { AlertDialogDescription } from "@radix-ui/react-alert-dialog";
import { Button } from "./ui/button";
import { Share } from "lucide-react";

const STEPS = ["welcome", "install", "done"];

type Platforms = "ios" | "android";

const instructions: Record<Platforms, { text: ReactNode }[]> = {
    ios: [
        { text: <>Öppna Hopeifier i din <strong>webbläsare</strong></> },
        { text: <span className="relative flex flex-row items-center">Tryck på&nbsp;<strong>dela-ikonen</strong> <Share className="absolute size-4 -right-5" /></span> },
        { text: <>Scrolla och välj <strong>"Lägg till på hemskärmen"</strong></> },
        { text: <>Tryck på <strong>"Lägg till"</strong> uppe till höger</> },
    ],
    android: [
        { text: <>Öppna Hopeifier i din <strong>webbläsare</strong></> },
        { text: <>Tryck på <strong>menyn</strong> (tre punkter uppe till höger)</> },
        { text: <>Välj <strong>"Lägg till på startskärmen"</strong></> },
        { text: <>Tryck på <strong>"Lägg till"</strong> för att bekräfta</> },
    ],
};

type HomeScreenOnboardingProps = {
    onSkip: () => void;
    onDone: () => void;
};
function OnboardingContent({ onSkip, onDone }: HomeScreenOnboardingProps) {
    const [step, setStep] = useState(0);
    const [platform, setPlatform] = useState<Platforms>("ios");

    const isLast = step === STEPS.length - 1;
    const isFirst = step === 0;

    function next() {
        if (!isLast) setStep((s) => s + 1);
        else {
            setStep(0);
            onDone?.();
        }
    }

    function back() {
        if (!isFirst) setStep((s) => s - 1);
        else {
            onSkip?.();
        }
    }

    return (
        <AlertDialogContent className="p-0 border-0 bg-white rounded-2xl shadow-2xl overflow-hidden max-w-sm">

            {/* Header */}
            <AlertDialogHeader className="bg-primary p-6 sm:text-center">
                <div className="w-14 h-14 rounded-[14px] bg-white flex items-center justify-center mx-auto mb-2.5 overflow-hidden">
                    <HopeifierLogo className="w-full h-full" />
                </div>

                <AlertDialogTitle className="text-[#E6EDF8] text-lg font-medium mb-1">Välkommen till Hopeifier</AlertDialogTitle>
                <AlertDialogDescription className="text-[#8BAAD4] text-sm">Lägg till appen på din hemskärm</AlertDialogDescription>

                {/* Step dots */}
                <div className="flex items-center justify-center gap-1.5 mt-3.5">
                    {STEPS.map((_, i) => (
                        <div
                            key={i}
                            className="h-1.5 rounded-full transition-all duration-200"
                            style={{
                                width: i === step ? 18 : 6,
                                background: i === step ? "#F5C518" : "#8BAAD4",
                                borderRadius: i === step ? 3 : "50%",
                            }}
                        />
                    ))}
                </div>
            </AlertDialogHeader>

            {/* Body */}
            <div className="p-6">

                {/* Step 0: Welcome */}
                {step === 0 && (
                    <div className="text-center">
                        <p className="text-sm text-gray-500 leading-relaxed">
                            Hopeifier fungerar som en app direkt i din webbläsare. Lägg till den på hemskärmen för snabb åtkomst – inga appbutiker behövs.
                        </p>
                    </div>
                )}

                {/* Step 1: Install guide */}
                {step === 1 && (
                    <div>
                        <p className="text-md font-medium text-gray-900 text-center mb-3">Välj din enhet</p>

                        {/* Platform tabs */}
                        <div className="flex gap-2 mb-3.5">
                            {(["ios", "android"] as const).map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setPlatform(p)}
                                    className={`flex-1 py-1.5 rounded-lg text-sm border transition-all duration-150 cursor-pointer ${platform === p
                                        ? "bg-primary text-white border-primary"
                                        : "bg-transparent text-gray-500 border-gray-300 hover:border-gray-400"
                                        }`}
                                >
                                    {p === "ios" ? "iPhone / iPad" : "Android"}
                                </button>
                            ))}
                        </div>

                        {/* Instruction cards */}
                        <div className="flex flex-col gap-2">
                            {instructions[platform].map((item, i) => (
                                <div
                                    key={i}
                                    className="bg-[#f4f6fb] rounded-sm px-3.5 py-2.5 flex items-center gap-2.5"
                                >
                                    <div className="size-5 rounded-full bg-primary text-white text-sm font-medium flex items-center justify-center shrink-0">
                                        {i + 1}
                                    </div>
                                    <p className="text-sm text-gray-800 leading-snug">{item.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 2: Done */}
                {step === 2 && (
                    <div className="text-center">
                        <p className="text-md font-medium text-gray-900 mb-2">Du är redo!</p>
                        <p className="text-sm text-gray-500 leading-relaxed">
                            Hopeifier finns nu på din hemskärm. Öppna den precis som en vanlig app – snabbt och enkelt.
                        </p>
                    </div>
                )}

                {/* Actions */}
                <div className="grid grid-cols-[1fr_2fr] gap-2 mt-5">
                    <Button
                        variant="outline"
                        onClick={back}
                    >
                        {isFirst ? "Hoppa över" : "Tillbaka"}
                    </Button>
                    <Button onClick={next}>
                        {isLast
                            ? "Klar!"
                            : isFirst
                                ? "Kom igång →"
                                : "Nästa"
                        }
                    </Button>
                </div>

            </div>
        </AlertDialogContent>
    );
}

export function HomeScreenOnboardingHandler() {
    const [onboardingOpen, setOnboardingOpen] = useState(
        !config.inStandalone &&
        localStorage.getItem("home-screen-onboarding-dismissed") !== "true"
    );
    const [showBottomBar, setShowBottomBar] = useState(
        !config.inStandalone &&
        localStorage.getItem("home-screen-onboarding-bottom-bar-dismissed") !== "true"
    );

    const closeDialog = () => {
        setOnboardingOpen(false);
        localStorage.setItem("home-screen-onboarding-dismissed", "true");
    };
    const dismissBottomBar = () => {
        setShowBottomBar(false);
        localStorage.setItem("home-screen-onboarding-bottom-bar-dismissed", "true");

    };

    return (
        <div>
            <AlertDialog open={onboardingOpen}>
                <OnboardingContent
                    onSkip={closeDialog}
                    onDone={closeDialog}
                />
            </AlertDialog>
            {showBottomBar && <div className="bottom-0 left-0 right-0 bg-white border-t border-gray-200 border-l-4 border-l-primary px-4 py-3 flex items-center gap-3">
                <HopeifierLogo size={24} />
                <p className="text-sm text-gray-700 flex-1">Lägg till Hopeifier på hemskärmen</p>
                <button onClick={() => setOnboardingOpen(true)} className="text-sm font-medium text-primary shrink-0">
                    Visa guide →
                </button>
                <button onClick={dismissBottomBar} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>}
        </div>
    );
}